import React, { Component } from "react";
import { DataTableToolbar } from "../../components/ui";
import AppBadge from "../../components/ui/AppBadge";
import { TransactionListItem } from "../../models/BoricaTransaction";
import { toastService } from "../../services/toastService";
import { boricaTransactionApiService } from "../../services/api/transactions/BoricaTransactionApiService";
import { utils } from "../../utils/utils";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Column } from "primereact/column";
import { stringFilterMatchModeOptions, numberFilterMatchModeOptions, statusFilterMatchModeOptions } from "../Paginator";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Pencil, Search } from "lucide-react";
import { Calendar } from "primereact/calendar";
import TransactionEditModal from "./TransactionEditModal";

// Map backend status values (string names or numeric) to display info
const STATUS_MAP: Record<string, { label: string; variant: "success" | "danger" | "warning" | "orange" | "neutral" | "default" }> = {
    "Success": { label: "Success", variant: "success" },
    "2": { label: "Success", variant: "success" },
    "Failed": { label: "Failed", variant: "danger" },
    "3": { label: "Failed", variant: "danger" },
    "Processing": { label: "Processing", variant: "warning" },
    "1": { label: "Processing", variant: "warning" },
    "PartialSuccess": { label: "Partial Success", variant: "orange" },
    "4": { label: "Partial Success", variant: "orange" },
    "Pending": { label: "Pending", variant: "neutral" },
    "0": { label: "Pending", variant: "neutral" },
};

const isSuccess = (status: string) => status === "Success" || status === "2";

interface TransactionsProps {
    extendedReportingEnabled?: boolean;
}

interface TransactionsState {
    transactions: TransactionListItem[];
    isLoading: boolean;
    totalRecords: number;
    selectedTransaction: TransactionListItem | null;
    isModalVisible: boolean;
    searchId: string;
    fromDate: Date | null;
    toDate: Date | null;
    isExporting: boolean;
}

export default class Transactions extends Component<TransactionsProps, TransactionsState> {
    private dataTable: ExtendedDataTable | null = null;
    private lastSearchRequest: SearchRequestBaseModel | null = null;

    constructor(props: TransactionsProps) {
        super(props);

        this.state = {
            transactions: [],
            isLoading: false,
            totalRecords: 0,
            selectedTransaction: null,
            isModalVisible: false,
            searchId: "",
            fromDate: null,
            toDate: null,
            isExporting: false,
        };
    }

    loadData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        // Inject FastPay Request ID filter if search field is set
        if (this.state.searchId.trim()) {
            searchRequest.filters = [
                ...searchRequest.filters.filter(f => f.columnName !== "fastPayRequestId"),
                { columnName: "fastPayRequestId", operator: "contains", value: this.state.searchId.trim() }
            ];
        }

        // Inject date range filters
        if (this.state.fromDate) {
            searchRequest.filters = [
                ...searchRequest.filters.filter(f => f.columnName !== "fromDate"),
                { columnName: "fromDate", operator: "equals", value: this.state.fromDate.toISOString() }
            ];
        }
        if (this.state.toDate) {
            searchRequest.filters = [
                ...searchRequest.filters.filter(f => f.columnName !== "toDate"),
                { columnName: "toDate", operator: "equals", value: this.state.toDate.toISOString() }
            ];
        }

        this.lastSearchRequest = { ...searchRequest };

        const result = await boricaTransactionApiService.searchTransactions(searchRequest);
        if (!result.success) {
            toastService.error(result.message);
        }
        this.setState({
            transactions: result.data ?? [],
            totalRecords: result.totalCount,
            isLoading: false,
        });
    };

    openModal = (transaction: TransactionListItem) => {
        this.setState({ selectedTransaction: transaction, isModalVisible: true });
    };

    closeModal = () => {
        this.setState({ selectedTransaction: null, isModalVisible: false });
    };

    onStatusUpdated = () => {
        this.closeModal();
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    searchById = () => {
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    clearSearch = () => {
        this.setState({ searchId: "" }, () => {
            if (this.dataTable) {
                this.dataTable.reset();
            }
        });
    };

    onDateApply = () => {
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    onDateClear = () => {
        this.setState({ fromDate: null, toDate: null }, () => {
            if (this.dataTable) {
                this.dataTable.reset();
            }
        });
    };

    exportCsv = async () => {
        if (!this.lastSearchRequest) return;

        this.setState({ isExporting: true });
        try {
            const exportRequest: SearchRequestBaseModel = {
                ...this.lastSearchRequest,
                pageNumber: 1,
                pageSize: 50000,
            };

            const result = await boricaTransactionApiService.exportTransactionsCsv(exportRequest);
            if (!result.success) {
                toastService.error(result.message || "Failed to export CSV");
                return;
            }

            utils.downloadCSVFromBase64(result.data, "fastpay_transactions_export.csv");
        } catch {
            toastService.error("Failed to export CSV");
        } finally {
            this.setState({ isExporting: false });
        }
    };

    renderHeader = (hasActiveFilters: boolean) => {
        const hasSearch = this.state.searchId.trim().length > 0;
        const hasDateFilter = this.state.fromDate !== null || this.state.toDate !== null;
        const { extendedReportingEnabled } = this.props;

        return (
            <DataTableToolbar
                onClear={() => { this.setState({ searchId: "", fromDate: null, toDate: null }, () => this.dataTable!.reset()); }}
                hasActiveFilters={hasActiveFilters || hasSearch || hasDateFilter}
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        {extendedReportingEnabled && (
                            <>
                                <Calendar
                                    value={this.state.fromDate}
                                    onChange={(e) => this.setState({ fromDate: e.value as Date | null })}
                                    dateFormat="dd.mm.yy"
                                    showIcon
                                    placeholder="From"
                                    maxDate={this.state.toDate || new Date()}
                                    inputClassName="p-inputtext-sm" inputStyle={{ marginRight: "4px" }}
                                    style={{ width: "185px" }}
                                />
                                <Calendar
                                    value={this.state.toDate}
                                    onChange={(e) => this.setState({ toDate: e.value as Date | null })}
                                    dateFormat="dd.mm.yy"
                                    showIcon
                                    placeholder="To"
                                    minDate={this.state.fromDate || undefined}
                                    maxDate={new Date()}
                                    inputClassName="p-inputtext-sm" inputStyle={{ marginRight: "4px" }}
                                    style={{ width: "185px" }}
                                />
                                <Button
                                    label="Apply"
                                    icon="pi pi-check"
                                    className="p-button-sm"
                                    disabled={!this.state.fromDate || !this.state.toDate}
                                    onClick={this.onDateApply}
                                />
                            </>
                        )}
                        {extendedReportingEnabled && <span className="border-l border-neutral-300 h-6 mx-1" />}
                        <span className="p-input-icon-left">
                            <InputText
                                value={this.state.searchId}
                                onChange={(e) => this.setState({ searchId: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") this.searchById(); }}
                                placeholder="FastPay Request ID"
                                className="p-inputtext-sm"
                                style={{ width: "180px" }}
                            />
                        </span>
                        <Button
                            icon={<Search size={16} />}
                            className="p-button-sm"
                            onClick={this.searchById}
                            tooltip="Search by ID"
                            tooltipOptions={{ position: "top" }}
                        />
                        {extendedReportingEnabled && (
                            <>
                            <span className="border-l border-neutral-300 h-6 mx-1" />
                            <Button
                                icon="pi pi-download"
                                className="p-button-sm p-button-outlined"
                                onClick={this.exportCsv}
                                loading={this.state.isExporting}
                                tooltip="Export CSV"
                                tooltipOptions={{ position: "top" }}
                                label="Export CSV"
                            />
                            </>
                        )}
                    </div>
                }
            />
        );
    };

    statusBodyTemplate = (rowData: TransactionListItem) => {
        const info = STATUS_MAP[rowData.status] ?? { label: rowData.status, variant: "default" as const };
        return <AppBadge variant={info.variant}>{info.label}</AppBadge>;
    };

    dateBodyTemplate = (rowData: TransactionListItem) => {
        if (!rowData.createdAt) return "-";
        const d = new Date(rowData.createdAt);
        if (isNaN(d.getTime())) return "-";
        return utils.formatDateTimeSofia(d);
    };

    actionBodyTemplate = (rowData: TransactionListItem) => {
        if (isSuccess(rowData.status)) {
            return null;
        }
        return (
            <Button
                icon={<Pencil size={16} />}
                className="p-button-text p-button-sm"
                onClick={(e) => { e.stopPropagation(); this.openModal(rowData); }}
                tooltip="Edit transaction status"
                tooltipOptions={{ position: "top" }}
            />
        );
    };

    onRowClick = (event: any) => {
        this.openModal(event.data as TransactionListItem);
    };

    render() {
        const { extendedReportingEnabled } = this.props;

        return (
            <>
                <ExtendedDataTable
                    header={this.renderHeader}
                    ref={(ref) => (this.dataTable = ref)}
                    value={this.state.transactions}
                    onChange={this.loadData}
                    loading={this.state.isLoading}
                    totalRecords={this.state.totalRecords}
                    defaultSortField="boricaTransactionId"
                    defaultSortOrder={SortOrderType.Descending}
                    onRowClick={this.onRowClick}
                >
                    <Column {...({ alwaysVisible: true, sortable: false } as any)} field="firstName" header="First Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column {...({ alwaysVisible: true, sortable: false } as any)} field="lastName" header="Last Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column {...({ alwaysVisible: true, sortable: false } as any)} field="email" header="Email" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column field="phoneNumber" header="Phone Number" filterMatchModeOptions={stringFilterMatchModeOptions} sortable={false} />
                    <Column field="service" header="Service" filterMatchModeOptions={stringFilterMatchModeOptions} sortable={false} />
                    {!extendedReportingEnabled && (
                        <Column field="accountNumber" header="Account Number" filterMatchModeOptions={stringFilterMatchModeOptions} sortable={false} />
                    )}
                    {extendedReportingEnabled && (
                        <Column field="identifier" header="Identifier" filterMatchModeOptions={stringFilterMatchModeOptions} sortable={false} />
                    )}
                    <Column field="amount" header="Amount" filterMatchModeOptions={numberFilterMatchModeOptions} />
                    {extendedReportingEnabled && (
                        <Column field="createdAt" header="Transaction Date" body={this.dateBodyTemplate} sortable />
                    )}
                    {extendedReportingEnabled && (
                        <Column field="boricaTransactionId" header="Borica Payment ID" filterMatchModeOptions={numberFilterMatchModeOptions} />
                    )}
                    <Column
                        field="status"
                        header="Status"
                        body={this.statusBodyTemplate}
                        filterMatchModeOptions={statusFilterMatchModeOptions}
                    />
                    <Column
                        header="Action"
                        body={this.actionBodyTemplate}
                        style={{ width: "80px", textAlign: "center" }}
                    />
                </ExtendedDataTable>

                <TransactionEditModal
                    transaction={this.state.selectedTransaction}
                    visible={this.state.isModalVisible}
                    onHide={this.closeModal}
                    onSaved={this.onStatusUpdated}
                />
            </>
        );
    }
}
